import { track } from './analytics';

// Wall-clock budget before a heavy action warns the user. The gate is
// time-based, not count-based: 30 CUE-DETR files are slower than 200
// compatibility checks, so each tab prices its own per-file cost below.
// Ten minutes: at two, a 13-track Cues run (about 3 min) already asked first,
// which is a coffee-length wait, not a large selection.
export const WARN_BUDGET_SECONDS = 600;

// Rough SERIAL seconds-per-file for each heavy action. confirmLargeSet
// divides by the tab's effective parallelism, so these stay honest as
// backends get faster — tune the serial number, not the product.
export const SET_COST = {
    cuePoints: 12,    // CUE-DETR ML inference, sequential — measured ~12 s/track on the
                      // prod ONNX path (decode+mel 1 s, cues 8.5 s, loops/grid 2 s); was 5,
                      // which put a 16 h run at "around 390 min"
    analyze: 2.5,     // key+BPM+loudness via the analysis fork pool
    fixMetadata: 2.5, // key+BPM via the Piscina pool
    loudness: 1.2,    // ffmpeg ebur128 per file
    fixTracks: 1,     // ffmpeg compatibility/convert check
    organize: 2.5,    // same enricher analysis as `analyze`
};

// Effective backend concurrency for tabs whose processing is parallel.
// Mirrors the backend pool sizing (os.cpus() - 2, floor 1). Sequential
// tabs (CUE-DETR) must pass parallelism: 1 instead.
export function effectiveParallelism() {
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
    return Math.max(1, cores - 2);
}

// "about 7 min" / "about 16 h"
export function formatEstimate(seconds) {
    const mins = Math.max(1, Math.round(seconds / 60));
    return mins < 90 ? `about ${mins} min` : `about ${Math.round(mins / 60)} h`;
}

// Loading more than this many tracks into a track-by-track tool (Cues & Loops)
// asks first — before the list is even built: thousands of rows are slow to
// show, and analysing all of them takes hours.
export const LOAD_WARN_COUNT = 500;

/**
 * Gate LOADING a big selection, before anything runs. Under `threshold`
 * returns 'all' with no UI; otherwise asks and returns 'all', 'first' (only
 * the first `firstN` — the throttle) or false (cancel).
 */
export async function confirmLargeLoad(toast, count, {
    secondsPerFile,
    parallelism = 1,
    sourceLabel = 'selection',
    tab = 'unknown',
    threshold = LOAD_WARN_COUNT,
    firstN = LOAD_WARN_COUNT,
} = {}) {
    if (!count || count <= threshold) return 'all';
    const estSeconds = (count * (secondsPerFile || 0)) / Math.max(1, parallelism);
    const noun = sourceLabel === 'library' ? 'Your library' : `This ${sourceLabel}`;
    const answer = await toast.confirm(
        `${noun} has ${count.toLocaleString()} tracks. They’re analysed one at a time — `
        + `about ${Math.round(secondsPerFile)} s each, so all of them would take ${formatEstimate(estSeconds)}. `
        + 'A playlist is usually the better place to start.',
        'Large selection',
        { confirmText: `Open all ${count.toLocaleString()}`, confirmVariant: 'primary', secondaryText: `Just the first ${firstN.toLocaleString()}` }
    );
    const choice = answer === 'secondary' ? 'first' : answer ? 'all' : false;
    track('large_set_warning_shown', {
        tab,
        file_count: count,
        estimated_minutes: Math.max(1, Math.round(estSeconds / 60)),
        source: sourceLabel,
        confirmed: !!choice,
        stage: 'load',
        choice: choice || 'cancel',
    });
    return choice;
}

const AUDIO_EXT_RE = /\.(mp3|wav|wave|aif|aiff|flac|m4a|ogg|aac|alac)$/i;

// Number of audio files a selection will actually process. Explicit file
// lists count directly; a selection containing a directory is expanded via
// the existing `cuepoints:resolveAudioFiles` IPC (recursive walk). Falls
// back to the raw length so the gate never blocks a run on a count error.
export async function countAudioFiles(paths) {
    if (!paths || !paths.length) return 0;
    if (paths.every((p) => AUDIO_EXT_RE.test(p))) return paths.length;
    try {
        const files = await window.electron?.cuePoints?.resolveAudioFiles?.(paths);
        return Array.isArray(files) ? files.length : paths.length;
    } catch {
        return paths.length;
    }
}

/**
 * Gate a heavy run on its ESTIMATED WALL-CLOCK time. Returns true to
 * proceed (immediately, with no UI, when the estimate is under budget),
 * false when the user cancels.
 *
 * @param toast        useToast() context (needs .confirm)
 * @param count        number of files about to be processed
 * @param opts.secondsPerFile  serial per-file cost (use SET_COST.*)
 * @param opts.parallelism     effective concurrency (default 1 = sequential)
 * @param opts.sourceLabel     'library' | 'playlist' | 'folder' | 'selection'
 * @param opts.tab             analytics tab id (e.g. 'cue_points')
 */
export async function confirmLargeSet(toast, count, {
    secondsPerFile,
    parallelism = 1,
    sourceLabel = 'selection',
    tab = 'unknown',
    budget = WARN_BUDGET_SECONDS,
} = {}) {
    if (!count || !secondsPerFile) return true;
    const estSeconds = (count * secondsPerFile) / Math.max(1, parallelism);
    if (estSeconds <= budget) return true;

    const mins = Math.max(1, Math.round(estSeconds / 60));
    const noun = sourceLabel === 'library' ? 'Your library' : `This ${sourceLabel}`;
    const confirmed = await toast.confirm(
        `${noun} has ${count.toLocaleString()} tracks — processing may take ${formatEstimate(estSeconds)}.`,
        'Large selection',
        { confirmText: 'Continue', confirmVariant: 'primary' }
    );
    track('large_set_warning_shown', {
        tab,
        file_count: count,
        estimated_minutes: mins,
        source: sourceLabel,
        confirmed: !!confirmed,
    });
    return !!confirmed;
}
